import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../../api/client";
import { theme, card, emptyFrame, btnPrimary, btnSecondary, inputStyle } from "../../theme";
import { DimensionHeading, SheetCard, StatusTag } from "../../components/Drawing";
import { ControlStatusBadge, displayRef } from "../../components/controlStatus";
import { frameworkLabel } from "../../nav/frameworkMeta";
import { LoadingBlock, ErrorBlock } from "../../components/Spinner";
import { fmtDate } from "../../utils/format";
import { fmtDateTime } from "../../lib/format";
import { AUDIT_TYPE_LABELS } from "./auditorShared";
import { POLICY_STATE_LABELS, type AuditSnapshotDetail, type AuditSnapshotSummary } from "./auditTools";

// Audit-date snapshots (backend services/auditSnapshots.ts): what was in force for
// this bureau's engagement on the audit date — scores, every SOA row, the version
// of each evidence document, the adopted policies and the latest AI verdicts —
// frozen and never edited. The worker takes one when a scheduled audit date is
// reached; an auditor can take one at any time. Any control's evidence trail can
// then be opened "as at" a snapshot from Controls & Findings.

function apiErrorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const payload = err.payload as { error?: unknown } | null;
    if (typeof payload?.error === "string") return payload.error;
  }
  return fallback;
}

export function AuditSnapshotsTab({ orgId, frameworkIds, readOnly = false }: { orgId: string; frameworkIds: string[]; readOnly?: boolean }) {
  const [snapshots, setSnapshots] = useState<AuditSnapshotSummary[] | null>(null);
  const [error, setError] = useState("");
  const [label, setLabel] = useState("");
  const [taking, setTaking] = useState(false);
  const [takeError, setTakeError] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(() => {
    setError("");
    api.get<{ snapshots: AuditSnapshotSummary[] }>(`/auditor/clients/${orgId}/snapshots`)
      .then((d) => setSnapshots(d.snapshots))
      .catch(() => setError("Could not load the snapshots."));
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  async function take() {
    setTaking(true);
    setTakeError("");
    try {
      const s = await api.post<AuditSnapshotSummary>(`/auditor/clients/${orgId}/snapshots`, { label: label.trim() || undefined });
      setLabel("");
      setSnapshots((prev) => [s, ...(prev ?? [])]);
      setOpenId(s.id);
    } catch (err) {
      setTakeError(apiErrorText(err, "Could not take the snapshot."));
    } finally {
      setTaking(false);
    }
  }

  if (error) return <ErrorBlock message={error} onRetry={load} />;
  if (!snapshots) return <LoadingBlock />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {!readOnly && <SheetCard label="Take a snapshot">
        <p style={{ fontSize: 12, color: theme.color.subtle, margin: "0 0 10px", lineHeight: 1.6 }}>
          Freezes what is in force now for {frameworkIds.map((id) => frameworkLabel(id)).join(", ") || "the standards engaged with your bureau"}:
          the scores, every Statement of Applicability row, the version of every evidence document, the adopted policies and the
          latest AI verdicts. A snapshot is also taken automatically when a scheduled audit date is reached. Snapshots cannot be
          edited or deleted.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            aria-label="Snapshot label"
            placeholder="Label, for example Stage 2 opening meeting"
            value={label}
            maxLength={200}
            onChange={(e) => setLabel(e.target.value)}
            style={{ ...inputStyle, flex: 1, minWidth: 220 }}
          />
          <button onClick={take} disabled={taking} style={{ ...btnPrimary, opacity: taking ? 0.6 : 1 }}>
            {taking ? "Taking snapshot" : "Take snapshot now"}
          </button>
        </div>
        {takeError && <div role="alert" style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 8 }}>{takeError}</div>}
      </SheetCard>}

      <div>
        <DimensionHeading count={snapshots.length}>Snapshots</DimensionHeading>
        {snapshots.length === 0 ? (
          <div style={{ ...emptyFrame, fontSize: 13, color: theme.color.faint }}>
            No snapshot yet. One is taken when the scheduled audit date is reached, or take one now.
          </div>
        ) : (
          <div style={{ ...card, padding: 0, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.color.line}`, textAlign: "left", color: theme.color.muted }}>
                  <th style={{ padding: "8px 12px" }}>Taken</th>
                  <th style={{ padding: "8px 12px" }}>Why</th>
                  <th style={{ padding: "8px 12px" }}>Standards</th>
                  <th style={{ padding: "8px 12px" }}>Score</th>
                  <th style={{ padding: "8px 12px" }}>Documents</th>
                  <th style={{ padding: "8px 12px" }} />
                </tr>
              </thead>
              <tbody>
                {snapshots.map((s) => (
                  <tr key={s.id} style={{ borderBottom: `1px solid ${theme.color.border}` }}>
                    <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>{fmtDateTime(s.takenAt)}</td>
                    <td style={{ padding: "8px 12px" }}>
                      {s.trigger === "schedule"
                        ? `${AUDIT_TYPE_LABELS[s.auditType ?? ""] ?? "Audit"} on ${fmtDate(s.stageDate)}`
                        : `Taken by ${s.takenByName ?? "an auditor"}`}
                      {s.label ? <div style={{ color: theme.color.faint }}>{s.label}</div> : null}
                    </td>
                    <td style={{ padding: "8px 12px" }}>{s.frameworkIds.map((id) => frameworkLabel(id)).join(", ")}</td>
                    <td style={{ padding: "8px 12px" }}>{s.overallScore === null ? "–" : `${s.overallScore}%`}</td>
                    <td style={{ padding: "8px 12px" }}>{s.documentCount ?? "–"}</td>
                    <td style={{ padding: "8px 12px", textAlign: "right" }}>
                      <button onClick={() => setOpenId(openId === s.id ? null : s.id)} style={{ ...btnSecondary, fontSize: 11, padding: "3px 10px" }}>
                        {openId === s.id ? "Close" : "Open"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {openId && <SnapshotDetail key={openId} orgId={orgId} snapshotId={openId} />}
    </div>
  );
}

function SnapshotDetail({ orgId, snapshotId }: { orgId: string; snapshotId: string }) {
  const [detail, setDetail] = useState<AuditSnapshotDetail | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get<AuditSnapshotDetail>(`/auditor/clients/${orgId}/snapshots/${snapshotId}`)
      .then(setDetail)
      .catch(() => setError("Could not open the snapshot."));
  }, [orgId, snapshotId]);

  if (error) return <div role="alert" style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>;
  if (!detail) return <LoadingBlock />;

  return (
    <SheetCard
      label={`Snapshot of ${fmtDateTime(detail.takenAt)}`}
      actions={detail.intact
        ? <StatusTag tone="met" title={`SHA-256 ${detail.digest}`}>Record intact</StatusTag>
        : <StatusTag tone="gap" title={`SHA-256 ${detail.digest}`}>Record does not match its digest</StatusTag>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {detail.scores.map((s) => (
            <div key={s.id} style={{ border: `1px solid ${theme.color.line}`, borderRadius: 3, padding: "8px 12px", minWidth: 140 }}>
              <div style={{ fontSize: 11, color: theme.color.muted }}>{frameworkLabel(s.id)}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: theme.color.heading }}>{s.score === null ? "–" : `${s.score}%`}</div>
              <div style={{ fontSize: 11, color: theme.color.faint }}>{s.implemented} of {s.total} in place</div>
            </div>
          ))}
        </div>

        <div style={{ fontSize: 12, color: theme.color.subtle }}>
          {detail.soa.length} SOA row{detail.soa.length === 1 ? "" : "s"} · {detail.evidence.length} evidence document{detail.evidence.length === 1 ? "" : "s"}
          {detail.verdicts ? ` · ${detail.verdicts.count} AI verdict${detail.verdicts.count === 1 ? "" : "s"} from the check of ${fmtDate(detail.verdicts.completedAt)}` : " · no compliance check had run"}.
          Open a control's evidence trail on Controls &amp; Findings and choose this snapshot under "As at" to see its documents, policy, verdict and history as they stood.
        </div>

        <div>
          <DimensionHeading as="h3" count={detail.evidence.length} style={{ fontSize: 13 }}>Evidence versions frozen</DimensionHeading>
          {detail.evidence.length === 0 ? (
            <div style={{ fontSize: 12, color: theme.color.faint }}>No document was filed under a standard in scope.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${theme.color.line}`, textAlign: "left", color: theme.color.muted }}>
                  <th style={{ padding: "6px 8px" }}>Document</th>
                  <th style={{ padding: "6px 8px" }}>Version</th>
                  <th style={{ padding: "6px 8px" }}>In the repository since</th>
                  <th style={{ padding: "6px 8px" }}>Filed under</th>
                </tr>
              </thead>
              <tbody>
                {detail.evidence.map((d) => (
                  <tr key={d.id} style={{ borderBottom: `1px solid ${theme.color.border}` }}>
                    <td style={{ padding: "6px 8px" }}>{d.fileName}</td>
                    <td style={{ padding: "6px 8px" }}>v{d.version}</td>
                    <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>{fmtDateTime(d.enteredAt)}</td>
                    <td style={{ padding: "6px 8px", color: theme.color.subtle }}>{d.tags.map((t) => displayRef(t.controlRef)).join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {detail.policies.length > 0 && (
          <div>
            <DimensionHeading as="h3" count={detail.policies.length} style={{ fontSize: 13 }}>Policies</DimensionHeading>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {detail.policies.map((p) => (
                <div key={p.templateId} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12 }}>
                  <span style={{ color: theme.color.heading, fontWeight: 600 }}>{p.title}</span>
                  <StatusTag tone={p.state === "adopted" ? "met" : p.state === "adopted_superseded" ? "partial" : "none"}>{POLICY_STATE_LABELS[p.state] ?? p.state}</StatusTag>
                </div>
              ))}
            </div>
          </div>
        )}

        <details>
          <summary style={{ fontSize: 12, color: theme.color.accentText, cursor: "pointer" }}>Statement of Applicability as it stood ({detail.soa.length} row{detail.soa.length === 1 ? "" : "s"})</summary>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
            {detail.soa.map((r) => (
              <div key={`${r.frameworkId}:${r.controlId}`} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12 }}>
                <span style={{ minWidth: 70, color: theme.color.muted, fontVariantNumeric: "tabular-nums" }}>{displayRef(r.controlId)}</span>
                <ControlStatusBadge status={r.status.toUpperCase()} />
                {r.applicable === false && <span style={{ color: theme.color.faint }}>not applicable</span>}
              </div>
            ))}
          </div>
        </details>
      </div>
    </SheetCard>
  );
}
